import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Package, Star } from "lucide-react";
import PageHero from "@/components/PageHero";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REMAINING = ["Full", "Half", "Low"];

export default function Pantry() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => client.get("/pantry").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    try { await client.post("/pantry", { name, category: "Other", remaining: "Full" }); setName(""); load(); }
    catch { toast.error("Couldn't add item"); }
  };
  const update = async (id, body) => { try { await client.patch(`/pantry/${id}`, body); load(); } catch { toast.error("Couldn't update"); } };
  const del = async (id) => { try { await client.delete(`/pantry/${id}`); load(); } catch { toast.error("Couldn't delete"); } };
  const low = items.filter((i) => i.remaining !== "Full");
  const restock = async () => {
    try {
      const { data: lists } = await client.get("/lists");
      const list = lists[0] || (await client.post("/lists", { name: "Weekly Groceries" })).data;
      const { data: full } = await client.get(`/lists/${list.id}`);
      const have = new Set((full.items || []).map((i) => i.name.toLowerCase()));
      const missing = low.filter((i) => !have.has(i.name.toLowerCase()));
      if (!missing.length) { toast.info("Those items are already on your list"); return; }
      await client.post(`/lists/${list.id}/items/bulk`, { text: missing.map((i) => i.name).join("\n") });
      toast.success(`Added ${missing.length} item${missing.length > 1 ? "s" : ""} to ${list.name}`);
    } catch { toast.error("Couldn't add to your list"); }
  };

  const color = { Full: "bg-[#EBF7EE] text-pp-forest", Half: "bg-pp-gold/20 text-[#8A5F00]", Low: "bg-[#FDF0ED] text-pp-error" };

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <PageHero image="/img/pantry.jpg" eyebrow="At home" title="My Pantry" subtitle="Know what's running low before you shop — no more doubles." testId="pantry-hero" />

      <div className="flex gap-2">
        <Input data-testid="pantry-add-input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a pantry item…" className="h-12 rounded-2xl bg-white border-0 pp-shadow" />
        <Button data-testid="pantry-add-button" onClick={add} className="h-12 rounded-2xl bg-pp-forest px-5"><Plus className="h-5 w-5" /></Button>
      </div>

      {low.length > 0 && (
        <button type="button" onClick={restock} data-testid="pantry-restock-button"
          className="w-full flex items-center justify-between rounded-3xl bg-pp-gold/20 border border-pp-gold/40 p-4 text-left transition-colors hover:bg-pp-gold/30">
          <span><b className="text-pp-text">{low.length} item{low.length > 1 ? "s" : ""} running low</b><span className="block text-sm text-pp-muted">Add them to your grocery list in one tap</span></span>
          <span className="rounded-full bg-pp-forest text-white text-sm font-bold px-4 py-2">Add to list</span>
        </button>
      )}

      {items.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center pp-shadow"><Package className="h-12 w-12 mx-auto text-pp-fresh" /><p className="mt-3 font-bold text-pp-text">Pantry is empty</p></div>
      ) : (
        <div className="bg-white rounded-3xl p-4 pp-shadow divide-y divide-pp-border">
          {items.map((it) => (
            <div key={it.id} data-testid={`pantry-item-${it.id}`} className="py-3 flex items-center gap-3">
              {it.staple && <Star className="h-4 w-4 text-pp-gold fill-pp-gold" />}
              <div className="flex-1 min-w-0"><p className="font-semibold text-pp-text truncate">{it.name}</p>
                <p className="text-xs text-pp-muted">Estimated remaining · {it.expiry ? `expires ${it.expiry}` : "no expiry"}</p></div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${color[it.remaining]}`}>{it.remaining}</span>
              <Select value={it.remaining} onValueChange={(v) => update(it.id, { remaining: v })}>
                <SelectTrigger data-testid={`pantry-remaining-${it.id}`} className="w-24 h-9 bg-white rounded-full text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{REMAINING.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => del(it.id)} data-testid={`pantry-delete-${it.id}`} className="text-pp-error p-1"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
